import React, { useState, useRef, useEffect } from "react";
import { useParams, useNavigate } from "react-router-dom";
import axios from "axios";
import { toast } from "sonner";
import SignatureCanvas from "react-signature-canvas";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { PenTool, Type, ArrowLeft, Check, RotateCcw } from "lucide-react";
import { API } from "@/lib/utils";

const LOGO_URL = "https://customer-assets.emergentagent.com/job_pool-lesson-book/artifacts/c9s2nl0s_rr%20logo.png";

const CONTRACT_TEXT = `
IMPORTANT INFORMATION

AM Lessons:
A parent/guardian is responsible for getting your child to camp. Bussing is not available for extended hours. For your convenience, your child's Swim Instructor will meet them in the front parking lot for valet drop off at 8:25am. Please have your child dressed in a bathing suit ready to swim. After the lesson, the Swim Instructor will ensure your child gets changed and reapplies sunscreen prior to escorting them to their camp group. Parents are not permitted to stay for morning lessons. It is your responsibility to bring your child to camp on time for their lessons as the lesson cannot be extended into the camp day. If your child normally takes the bus to camp, please alert the Bus Counselor to not pick up on lesson days.

PM Lessons:
If your child has a 4:00pm lesson, their counselor will bring them directly to the pool. If your child has a 4:30pm lesson, their counselor will bring them to our Camp Aftercare Program, where they will be supervised until it's time for their lesson. It is recommended parents/guardians arrive for pick up 10 minutes prior to the end of the lesson. Pick up is directly at the pool, unless your child is scheduled for Aftercare post lesson. Bussing is not available for extended hours. Please note, parents are not permitted to enter the campus until the buses have departed. While you are welcome to watch your child's private lesson, we ask that you do not interfere with the instructor during their lesson.

Cancellations, Make Ups, Refund Policy:
If a family cancels for any reason (i.e. illness, vacation…), our Swim Instructor will do their best to reschedule. However, a make up lesson is not guaranteed. There are no refunds for missed lessons. If you know your child will miss a lesson, please notify us as soon as possible. Advanced absences should be emailed to aquatics@rollingriver.com. Notify the Camp Office directly via phone (516-593-2267) or email (Camp@rollingriver.com) for day-of absences. If the camp cancels for any reason (i.e. weather, instructor availability…), a make up lesson will be scheduled or a refund for the missed lesson will be provided. Communication regarding canceled lessons will be through email, text and/or phone. Your instructor will reach out to you directly to schedule a make up lesson.

Agreement, Waiver, and Release:
I, the Parent, have carefully read the description of Lessons for which we are registering, and, in consideration for being permitted by the Instructor to participate in the activity, I hereby waive, release, and discharge any and all claims for damages for personal injury, death, or property damage which I may have, or which may hereafter inure to me, as a result of participation in Lessons. I hereby acknowledge that participation in Lessons does not guarantee that my child will be water-safe. This release is intended to discharge in advance the Instructor and Rolling River Day Camp from any and all liability arising out of, or connected in any way, with my participation in the Lessons, even though that liability may arise out of negligence or carelessness on the part of Instructor. It is understood that this Activity involves an element of risk and danger of accidents, and knowing those risks, I hereby assume those risks. It is further agreed that this waiver, release and assumption of risk is to be binding on my heirs and assigns. I agree to indemnify and to hold Instructors and Rolling River Day Camp free and harmless from any loss, liability, damage, cost, or expense which they may incur as the result of my or my child's injury or property damage that I may sustain while participating in the Activity.
`;

export default function ContractPage() {
  const { registrationId } = useParams();
  const navigate = useNavigate();
  const sigCanvas = useRef(null);
  const [registration, setRegistration] = useState(null);
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [signatureType, setSignatureType] = useState("canvas");
  const [typedSignature, setTypedSignature] = useState("");

  useEffect(() => {
    fetchRegistration();
  }, [registrationId]);

  const fetchRegistration = async () => {
    try {
      const res = await axios.get(`${API}/registration/${registrationId}`);
      setRegistration(res.data);
      if (res.data.status === "signed" || res.data.status === "paid") {
        navigate(`/confirmation/${registrationId}`);
      }
    } catch (err) {
      toast.error("Registration not found");
      navigate("/");
    } finally {
      setLoading(false);
    }
  };

  const clearSignature = () => {
    if (sigCanvas.current) {
      sigCanvas.current.clear();
    }
  };

  const handleSubmit = async () => {
    let signature = "";
    
    if (signatureType === "canvas") {
      if (sigCanvas.current?.isEmpty()) {
        toast.error("Please sign the contract");
        return;
      }
      signature = sigCanvas.current.toDataURL();
    } else {
      if (!typedSignature.trim()) {
        toast.error("Please type your signature");
        return;
      }
      signature = typedSignature.trim();
    }

    setSubmitting(true);
    try {
      await axios.post(`${API}/sign-contract`, {
        registration_id: registrationId,
        signature,
        signature_type: signatureType,
      });
      toast.success("Contract signed successfully!");
      navigate(`/confirmation/${registrationId}`);
    } catch (err) {
      toast.error("Failed to sign contract. Please try again.");
    } finally {
      setSubmitting(false);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-[#FDFBF7]">
        <div className="text-center">
          <div className="w-16 h-16 border-4 border-[#109FE9] border-t-transparent rounded-full animate-spin mx-auto mb-4" />
          <p className="text-slate-600">Loading contract...</p>
        </div>
      </div>
    );
  }

  if (!registration) return null;

  return (
    <div className="min-h-screen bg-[#FDFBF7]">
      <div className="pattern-bg" />
      
      {/* Header */}
      <header className="bg-white shadow-sm border-b border-slate-100">
        <div className="max-w-4xl mx-auto px-4 py-4 flex items-center justify-between">
          <img src={LOGO_URL} alt="Rolling River Day Camp" className="h-14" />
          <Button variant="ghost" onClick={() => navigate("/")} className="text-slate-600">
            <ArrowLeft className="w-4 h-4 mr-2" /> Back
          </Button>
        </div>
      </header>

      <main className="max-w-4xl mx-auto px-4 py-8">
        <div className="card animate-fade-in">
          <h1 className="font-heading text-3xl font-bold text-[#109FE9] mb-2">
            2026 Private Swim Lesson Contract
          </h1>
          <p className="text-slate-600 mb-6">
            Please read the contract carefully and sign below.
          </p>

          {/* Registration Summary */}
          <div className="mb-6">
            <h2 className="font-heading text-xl font-bold text-slate-800 mb-4">Your Lesson Details</h2>
            
            <div className="bg-slate-50 rounded-xl p-4 mb-4">
              <p className="text-lg"><strong>Child:</strong> {registration.child_first_name} {registration.child_last_name}</p>
              <p><strong>Parent:</strong> {registration.parent_first_name} {registration.parent_last_name}</p>
            </div>

            <div className="bg-[#E6F5FD] border-2 border-[#109FE9] rounded-xl p-4 mb-4">
              <div className="grid grid-cols-2 gap-2 mb-3">
                <p className="text-lg"><strong>Day:</strong> {registration.confirmed_day?.charAt(0).toUpperCase() + registration.confirmed_day?.slice(1)}</p>
                <p className="text-lg"><strong>Time:</strong> {registration.confirmed_time}</p>
                <p><strong>Lessons:</strong> {registration.confirmed_dates?.length || registration.choice1_dates?.length} lessons</p>
                <p><strong>Price:</strong> ${registration.confirmed_price?.toFixed(2) || registration.choice1_price?.toFixed(2)}</p>
                <p><strong>Lifeguard:</strong> {registration.assigned_instructor || registration.lifeguard_preference}</p>
              </div>
              <div className="bg-white rounded-lg p-3">
                <p className="font-semibold text-[#0E8ACB] text-sm mb-2">Your Lesson Dates:</p>
                <div className="flex flex-wrap gap-2">
                  {(registration.confirmed_dates || registration.choice1_dates)?.map((date, idx) => (
                    <span key={idx} className="px-3 py-1 bg-[#E6F5FD] border border-[#109FE9] rounded text-sm font-medium">
                      {date}
                    </span>
                  ))}
                </div>
              </div>
            </div>
          </div>

          {/* Contract Text */}
          <div className="bg-slate-50 rounded-xl p-6 mb-6 max-h-96 overflow-y-auto">
            <pre className="whitespace-pre-wrap font-body text-sm text-slate-700 leading-relaxed">
              {CONTRACT_TEXT}
            </pre>
          </div>

          {/* Signature Section */}
          <div className="border-t border-slate-200 pt-6">
            <h2 className="font-heading text-xl font-bold text-slate-800 mb-4">
              Sign Below
            </h2>

            <Tabs value={signatureType} onValueChange={setSignatureType} className="w-full">
              <TabsList className="grid w-full grid-cols-2 mb-4">
                <TabsTrigger value="canvas" className="flex items-center gap-2">
                  <PenTool className="w-4 h-4" /> Draw Signature
                </TabsTrigger>
                <TabsTrigger value="typed" className="flex items-center gap-2">
                  <Type className="w-4 h-4" /> Type Signature
                </TabsTrigger>
              </TabsList>

              <TabsContent value="canvas">
                <div className="relative">
                  <SignatureCanvas
                    ref={sigCanvas}
                    canvasProps={{
                      className: "signature-pad w-full",
                      "data-testid": "signature-canvas"
                    }}
                    backgroundColor="#f8fafc"
                  />
                  <Button
                    type="button"
                    variant="ghost"
                    size="sm"
                    onClick={clearSignature}
                    className="absolute top-2 right-2"
                    data-testid="clear-signature-btn"
                  >
                    <RotateCcw className="w-4 h-4 mr-1" /> Clear
                  </Button>
                </div>
                <p className="text-xs text-slate-500 mt-2 text-center">
                  Draw your signature above using your mouse or touch screen
                </p>
              </TabsContent>

              <TabsContent value="typed">
                <div>
                  <Label className="text-sm font-bold text-slate-700 uppercase tracking-wider mb-2 block">
                    Type Your Full Name as Signature
                  </Label>
                  <Input
                    data-testid="typed-signature-input"
                    className="form-input w-full text-2xl font-signature"
                    style={{ fontFamily: "'Dancing Script', cursive, serif" }}
                    placeholder="Your Full Name"
                    value={typedSignature}
                    onChange={(e) => setTypedSignature(e.target.value)}
                  />
                </div>
              </TabsContent>
            </Tabs>

            <div className="mt-6 p-4 bg-yellow-50 rounded-xl border border-yellow-200">
              <p className="text-sm text-yellow-800">
                <strong>Parent/Guardian:</strong> {registration.parent_first_name} {registration.parent_last_name}
              </p>
              <p className="text-sm text-yellow-800 mt-1">
                By signing, I agree to the terms and conditions stated in this contract.
              </p>
            </div>

            <Button
              onClick={handleSubmit}
              disabled={submitting}
              className="btn-primary w-full mt-6"
              data-testid="sign-contract-btn"
            >
              {submitting ? (
                "Signing..."
              ) : (
                <>
                  <Check className="w-5 h-5 mr-2" /> Sign Contract
                </>
              )}
            </Button>
          </div>
        </div>
      </main>
    </div>
  );
}
